import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Box, PlaneTakeoff } from 'lucide-react';

const BLUE = '#3C59B2';

type AudienceKey = 'builder' | 'agent';
type FormatKey = 'matterport' | 'drone';

const content: Record<
  AudienceKey,
  Record<
    FormatKey,
    {
      challenges: string[];
      solutions: string[];
    }
  >
> = {
  builder: {
    matterport: {
      challenges: [
        "You're launching a new development, every unit needs its own video before buyers visit",
        'Professional video production is slow, expensive, and hard to scale',
        "Buyers expect walkthrough videos to see a property's flow before visiting",
      ],
      solutions: [
        'Send your Matterport links, we handle the rest',
        'One professional video per unit, ready to share with buyers',
        'Delivered in 24h',
      ],
    },
    drone: {
      challenges: [
        'You have Matterport tours and drone clips, but no one to turn them into one video',
        'You want a professional video that sells the neighborhood and the interior',
        "Professional editing takes time you don't have",
      ],
      solutions: [
        'Send your Matterport links + drone clips, we handle the rest',
        'Aerial exterior and full interior walkthrough, seamlessly combined',
        'Delivered in 24h',
      ],
    },
  },
  agent: {
    matterport: {
      challenges: [
        "Every listing needs a walkthrough video, and you can't afford to wait",
        "Posting one video per listing shouldn't take days to organize",
        "Hiring a video team for every property isn't sustainable",
      ],
      solutions: [
        'Send your Matterport links, we handle the rest',
        'Polished, branded or unbranded videos for every listing',
        'Delivered in 24h',
      ],
    },
    drone: {
      challenges: [
        'You have Matterport tours and drone clips, but two separate files',
        'You want one seamless video, not two disconnected pieces',
        "Editing them together professionally takes hours you don't have",
      ],
      solutions: [
        'Send your Matterport link + drone clips, we handle the merge',
        'Aerial exterior and full interior walkthrough, seamlessly combined',
        'Delivered in 24h',
      ],
    },
  },
};

const formatOptions: { key: FormatKey; label: string; icon: React.ReactNode }[] = [
  { key: 'matterport', label: 'Matterport', icon: <Box size={15} /> },
  { key: 'drone', label: 'Matterport + drone clips', icon: <PlaneTakeoff size={15} /> },
];

export default function AudienceToggle() {
  const [tab, setTab] = useState<AudienceKey>('builder');
  const [format, setFormat] = useState<FormatKey>('matterport');

  const { challenges, solutions } = useMemo(() => content[tab][format], [tab, format]);

  return (
    <section className="py-12 sm:py-16 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10">
          <motion.h2
            className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            From Matterport to video. In 1 day
          </motion.h2>
        </div>
        <div className="mx-auto max-w-5xl flex flex-col items-center gap-6">
          {/* Toggle audience */}
          <div className="w-full flex flex-col items-center gap-3">
            <span className="text-base text-gray-500 text-center">
              <span className="font-semibold text-gray-700 mr-1">1.</span> You are:
            </span>
            <div className="flex rounded-full p-1 gap-1 bg-[#eef0f8] w-full sm:w-auto justify-center">
              {[
                { key: 'builder' as const, label: 'Home Builder' },
                { key: 'agent' as const, label: 'Agent / Broker' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-5 sm:px-6 py-2.5 rounded-full text-base font-semibold transition-all duration-150 w-1/2 sm:w-auto ${
                    tab === key ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  style={tab === key ? { background: BLUE } : undefined}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Toggle format */}
          <div className="w-full flex flex-col items-center gap-3">
            <span className="text-base text-gray-500 text-center">
              <span className="font-semibold text-gray-700 mr-1">2.</span> You already have:
            </span>
            <div className="flex gap-2 flex-wrap justify-center">
              {formatOptions.map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => setFormat(key)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-base font-semibold border transition-all duration-150 ${
                    format === key ? 'text-white shadow-sm' : 'text-[#3C59B2] hover:bg-slate-50'
                  }`}
                  style={
                    format === key
                      ? { background: BLUE, borderColor: BLUE }
                      : { background: '#fff', borderColor: '#a0aec0' }
                  }
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Card challenge / solution */}
          <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 rounded-3xl border border-[#E2E8F0] overflow-hidden bg-white shadow-sm">
            <div className="p-6 sm:p-8 md:border-r border-[#E2E8F0]">
              <p className="text-sm font-bold text-red-500 uppercase tracking-wide mb-5 text-center">Your challenge</p>
              <ul className="flex flex-col gap-4">
                {challenges.map((item, i) => (
                  <li key={i} className="flex gap-2.5 text-base text-gray-700 leading-relaxed">
                    <span className="text-red-400 mt-0.5 font-bold">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 sm:p-8 bg-slate-50">
              <p className="text-sm font-bold text-green-600 uppercase tracking-wide mb-5 text-center">With 3DtoVideo</p>
              <ul className="flex flex-col gap-4">
                {solutions.map((item, i) => (
                  <li key={i} className="flex gap-2.5 text-base text-gray-700 leading-relaxed">
                    <span className="text-green-600 mt-0.5 font-bold">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

